import { useState, useEffect } from "react";
import { openPaywall } from "../components/Paywall";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, 
  Heart, 
  X, 
  Star, 
  MessageCircle, 
  MapPin, 
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Flag,
  AlertTriangle,
  Zap,
  ShieldOff,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import ProfileAttributes from "../components/ProfileAttributes";
import SuperLikeModal from "../components/SuperLikeModal";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

import { API } from "../config";
import { tx } from "../lib/tx";

const REPORT_REASONS = [
  { value: "fake_profile", label: "Profil fake / usurpation d'identité" },
  { value: "harassment", label: "Harcèlement ou intimidation" },
  { value: "threats", label: "Menaces ou comportement dangereux" },
  { value: "insults", label: "Insultes ou propos haineux" },
  { value: "inappropriate_content", label: "Contenu inapproprié" },
  { value: "spam", label: "Spam ou arnaque" },
  { value: "underage", label: "Personne mineure" },
  { value: "other", label: "Autre raison" }
];

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [messageStatus, setMessageStatus] = useState({ free_messages_remaining: 5, credits: 0 });
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reporting, setReporting] = useState(false);
  const [blockAlso, setBlockAlso] = useState(false);
  const [blocking, setBlocking] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchMessageStatus();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/users/${userId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        alert(tx("Profil introuvable"));
        navigate(-1);
      }
    } catch (error) {
      console.error("Fetch profile error:", error);
      alert(tx("Erreur lors du chargement du profil"));
    } finally {
      setLoading(false);
    }
  };

  const fetchMessageStatus = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/messages/status/daily`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessageStatus(data);
      }
    } catch (error) {
      console.error("Message status error:", error);
    }
  };

  const handleAction = async (actionType) => {
    try {
      const token = localStorage.getItem("session_token");
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      
      // Check credits for super_like
      if (actionType === "superlike" && (storedUser?.credits || 0) < 5) {
        openPaywall("quota");
        return;
      }
      
      const response = await fetch(`${API}/likes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ 
          target_user_id: userId,
          action_type: actionType === "superlike" ? "super_like" : actionType
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.is_match) {
          alert(tx("🎉 C'est un Match !"));
          navigate(`/chat/${userId}`);
        } else {
          alert(tx(
            actionType === "like" ? "❤️ Like envoyé !" : 
            actionType === "superlike" ? "⭐ Super Like envoyé !" : 
            "Profil passé"
          ));
          navigate(-1);
        }
      } else {
        const error = await response.json();
        if (response.status !== 402) alert(error.detail || tx("Erreur lors de l'action"));
      }
    } catch (error) {
      console.error("Action error:", error);
      alert(tx("Erreur lors de l'action"));
    }
  };

  const handleSendMessage = () => {
    navigate(`/chat/${userId}`);
  };

  const handleReport = async () => {
    if (!selectedReason) {
      alert(tx("Veuillez sélectionner une raison"));
      return;
    }

    setReporting(true);
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/reports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          reported_user_id: userId,
          reason: selectedReason,
          details: reportDetails
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Also block user if checkbox was checked
        if (blockAlso) {
          await handleBlockUser();
        }
        alert(data.message || tx("Signalement envoyé"));
        setReportDialogOpen(false);
        setSelectedReason("");
        setReportDetails("");
        setBlockAlso(false);
        if (blockAlso) {
          navigate(-1); // Go back if blocked
        }
      } else {
        alert(data.detail || tx("Erreur lors du signalement"));
      }
    } catch (error) {
      console.error("Report error:", error);
      alert(tx("Erreur lors du signalement"));
    } finally {
      setReporting(false);
    }
  };

  const handleBlockUser = async () => {
    setBlocking(true);
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/blocked-users/${userId}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
      });

      if (response.ok) {
        return true;
      }
      return false;
    } catch (error) {
      console.error("Block error:", error);
      return false;
    } finally {
      setBlocking(false);
    }
  };

  const handleBlockOnly = async () => {
    setBlocking(true);
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/blocked-users/${userId}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
      });

      if (response.ok) {
        setReportDialogOpen(false);
        alert(tx("✅ Membre bloqué avec succès ! Il ne pourra plus vous contacter."));
        navigate(-1);
      } else {
        const data = await response.json();
        alert(data.detail || tx("Erreur lors du blocage"));
      }
    } catch (error) {
      console.error("Block error:", error);
      alert(tx("Erreur lors du blocage"));
    } finally {
      setBlocking(false);
    }
  };

  const nextPhoto = () => {
    if (profile?.photos && currentPhotoIndex < profile.photos.length - 1) {
      setCurrentPhotoIndex(prev => prev + 1);
    }
  };

  const prevPhoto = () => {
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(prev => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">{tx("Profil introuvable")}</p>
      </div>
    );
  }

  // Priorité: main_photo (HD), puis photos array, puis picture
  const mainPhoto = profile.main_photo || profile.picture || '/default-avatar.png';
  const photos = profile.main_photo 
    ? [profile.main_photo, ...(profile.photos || []).filter(p => p !== profile.main_photo)]
    : (profile.photos && profile.photos.length > 0) 
      ? profile.photos 
      : [profile.picture || '/default-avatar.png'];

  return (
    <div className="min-h-screen bg-background" data-testid="user-profile-page">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/60 to-transparent">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <button
            onClick={() => setReportDialogOpen(true)}
            className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-red-500/50 transition-colors"
            data-testid="report-btn"
          >
            <Flag className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Photo Gallery */}
      <div className="relative h-[70vh] bg-black">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPhotoIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0"
          >
            <img
              src={photos[currentPhotoIndex]}
              alt={profile.name}
              className="w-full h-full object-cover"
            />
          </motion.div>
        </AnimatePresence>

        {/* Photo Navigation */}
        {photos.length > 1 && (
          <>
            {/* Photo Indicators */}
            <div className="absolute top-16 left-0 right-0 flex justify-center gap-1.5 px-4">
              {photos.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1 rounded-full transition-all ${
                    idx === currentPhotoIndex 
                      ? "bg-white w-8" 
                      : "bg-white/40 w-6"
                  }`}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevPhoto}
              className={`absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white transition-opacity ${
                currentPhotoIndex === 0 ? "opacity-30" : "hover:bg-black/50"
              }`}
              disabled={currentPhotoIndex === 0}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextPhoto}
              className={`absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white transition-opacity ${
                currentPhotoIndex === photos.length - 1 ? "opacity-30" : "hover:bg-black/50"
              }`}
              disabled={currentPhotoIndex === photos.length - 1}
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Tap Zones */}
            <div 
              className="absolute inset-y-0 left-0 w-1/3" 
              onClick={prevPhoto}
            />
            <div 
              className="absolute inset-y-0 right-0 w-1/3" 
              onClick={nextPhoto}
            />
          </>
        )}

        {/* Photo Counter */}
        {photos.length > 1 && (
          <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-sm">
            {currentPhotoIndex + 1} / {photos.length}
          </div>
        )}

        {/* Verified Badge */}
        {profile.is_verified && (
          <div className="absolute top-16 right-4 flex items-center gap-1 px-3 py-1.5 bg-green-500/90 backdrop-blur-sm rounded-full">
            <BadgeCheck className="w-4 h-4 text-white" />
            <span className="text-sm font-medium text-white">{language === "en" ? "Verified" : "Vérifié"}</span>
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </div>

      {/* Profile Info */}
      <div className="relative -mt-8 bg-background rounded-t-3xl px-6 pt-6 pb-32">
        {/* Name & Basic Info */}
        <div className="flex items-center gap-3 mb-4">
          <h1 className="font-heading text-3xl font-bold text-white">
            {profile.name}, {profile.age || 25}
          </h1>
          {profile.is_verified && (
            <BadgeCheck className="w-6 h-6 text-green-500" />
          )}
        </div>

        {/* Location */}
        {profile.location && (
          <div className="flex items-center gap-2 text-muted-foreground mb-4">
            <MapPin className="w-4 h-4" />
            <span>{profile.location}</span>
            {profile.distance && (
              <span className="text-primary">• {profile.distance} km</span>
            )}
          </div>
        )}

        {/* Bio */}
        {profile.bio && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-white mb-2">{language === "en" ? "About" : "À propos"}</h3>
            <p className="text-muted-foreground leading-relaxed">{profile.bio}</p>
          </div>
        )}

        {/* Interests */}
        {profile.interests && profile.interests.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-white mb-3">{tx("Centres d'intérêt")}</h3>
            <div className="flex flex-wrap gap-2">
              {profile.interests.map((interest, i) => (
                <span 
                  key={i}
                  className="px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm"
                >
                  {interest}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Profile Attributes */}
        <ProfileAttributes user={profile} language={language === "en" ? "en" : "fr"} />

        {/* Looking For */}
        {profile.looking_for && (
          <div className="mb-6 mt-6">
            <h3 className="text-sm font-semibold text-white mb-2">{tx("Recherche")}</h3>
            <p className="text-muted-foreground">{profile.looking_for}</p>
          </div>
        )}
      </div>

      {/* Fixed Action Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-background via-background to-transparent pt-8 pb-6 px-6">
        <div className="max-w-md mx-auto">
          {/* Main Actions */}
          <div className="flex items-center justify-center gap-4 mb-4">
            {/* Pass */}
            <button
              onClick={() => handleAction("pass")}
              className="w-14 h-14 rounded-full bg-card border border-white/10 flex items-center justify-center text-white/60 hover:text-red-500 hover:border-red-500/50 transition-all"
              data-testid="pass-btn"
            >
              <X className="w-7 h-7" />
            </button>

            {/* Super Like */}
            <button
              onClick={() => handleAction("superlike")}
              className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all"
              data-testid="superlike-btn"
            >
              <Star className="w-8 h-8 fill-current" />
            </button>

            {/* Like */}
            <button
              onClick={() => handleAction("like")}
              className="w-14 h-14 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center text-white shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 transition-all"
              data-testid="like-btn"
            >
              <Heart className="w-7 h-7 fill-current" />
            </button>
          </div>

          {/* Message Button */}
          <Button
            onClick={handleSendMessage}
            className="w-full rounded-xl h-12 bg-blue-500 hover:bg-blue-600"
            data-testid="message-btn"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            {language === "en" ? "Send a message" : "Envoyer un message"}
            <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
              {messageStatus.free_messages_remaining > 0 
                ? (language === "en" ? `${messageStatus.free_messages_remaining} free` : `${messageStatus.free_messages_remaining} gratuit${messageStatus.free_messages_remaining > 1 ? 's' : ''}`) 
                : (language === "en" ? '2 credits' : '2 crédits')}
            </span>
          </Button>
        </div>
      </div>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <DialogTitle className="text-center text-white text-xl">
              {tx("Signaler ce profil")}
            </DialogTitle>
            <DialogDescription className="text-center">
              {tx("Aidez-nous à maintenir une communauté sûre. Sélectionnez la raison de votre signalement.")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 my-4 max-h-64 overflow-y-auto">
            {REPORT_REASONS.map((reason) => (
              <button
                key={reason.value}
                onClick={() => setSelectedReason(reason.value)}
                className={`w-full p-3 rounded-xl text-left transition-all ${
                  selectedReason === reason.value
                    ? "bg-red-500/20 border border-red-500 text-white"
                    : "bg-white/5 border border-white/10 text-muted-foreground hover:bg-white/10"
                }`}
              >
                {tx(reason.label)}
              </button>
            ))}
          </div>

          {selectedReason && (
            <textarea
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              placeholder={tx("Détails supplémentaires (optionnel)...")}
              className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-muted-foreground resize-none"
              rows={3}
            />
          )}

          {/* Block user checkbox */}
          <div className="flex items-center gap-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl mt-4">
            <input
              type="checkbox"
              id="block-user"
              checked={blockAlso}
              onChange={(e) => setBlockAlso(e.target.checked)}
              className="w-5 h-5 rounded accent-orange-500"
            />
            <label htmlFor="block-user" className="text-sm text-orange-200 cursor-pointer">
              <span className="font-medium">{tx("Bloquer également ce membre")}</span>
              <p className="text-xs text-orange-300/70 mt-0.5">{tx("Il ne pourra plus vous contacter ni voir votre profil")}</p>
            </label>
          </div>

          {/* Block only button */}
          <button
            onClick={handleBlockOnly}
            disabled={blocking}
            className="w-full mt-3 p-3 bg-white/5 border border-white/10 rounded-xl text-muted-foreground hover:bg-white/10 hover:text-white transition-all flex items-center justify-center gap-2"
          >
            <ShieldOff className="w-4 h-4" />
            {blocking ? tx("Blocage...") : tx("Bloquer sans signaler")}
          </button>

          <DialogFooter className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setReportDialogOpen(false)}
              className="flex-1 rounded-full"
            >
              {tx("Annuler")}
            </Button>
            <Button
              onClick={handleReport}
              disabled={!selectedReason || reporting}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full"
              data-testid="confirm-report-btn"
            >
              {reporting ? tx("Envoi...") : tx("Signaler")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
